import {
  Box,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useMemo, useState } from "react";
import "./Order.css";
import { useGetMenuQuery } from "store/apis/MenusApi";
import { EditSkeleton } from "components/admin/admin.overlays";
import { useGetMealAddOnsQuery, useGetPendingMealsQuery } from "store/apis/MealsApi";
import dayjs from "dayjs";
import {
  useGetResidentActiveMealsByIdQuery,
  useGetResidentInfoByIdQuery,
  useGetResidentsAllQuery,
} from "store/apis/ResidentsApi";
import { useGetDiningAreasQuery } from "store/apis/DiningAreasApi";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGuestInfo,
  selectLastMealSelected,
  selectMenuInfo,
  selectOrderWorkflow,
  setLastMealSelected,
  setSelectedMeal,
} from "store/slices/orderSlice/OrderSlice";
import InfoModal from "components/shared/infoModal/InfoModal";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import OrderConfirmModal from "components/shared/orderConfirmModal/OrderConfirmModal";
import {
  useAddHeldOrderMutation,
  useAddResidentOrderMutation,
  useDeleteOrderMutation,
  useDeleteHeldOrderMutation,
  useGetFacilityGuestChargeByIdQuery,
} from "store/apis/OrdersApi";
import {
  DEFAULT_EMPTY,
  DIRECT_PRINT_DATE_OPTIONS,
} from "store/store.constants";
import ReasonModal from "components/shared/reasonModal/ReasonModal";
import { WarningText } from "components/shared/WarningText";
import { BaseButton } from "components/shared/BaseButton";
import PlanLimitModal from "components/shared/planLimitModal/PlanLimitModal";
import ResidentDetailSection from "./ResidentDetailSection";
import OrderMenuDetails from "components/shared/orderMenuDetails/OrderMenuDetails";
import { getMealSetupStatus, findActiveMealForDiningArea, findMealBySetupId } from "utils/mealSetups";

//Cannot use CSS className for print output, must use inline styles
const printStyles = {
  printGeneral: {
    display: "flex",
    paddingBottom: "0px",
    paddingTop: "0px",
  },
  printDiv: {
    paddingBottom: "0px",
    paddingTop: "0px",
  },
};

export default function Order({
  residentId,
  tableSideSetupId,
  isGuest,
  isDirectPrint,
  allowMealCardNotes,
  allowMealAddons,
  currentUserData,
  handlePrint,
  isAutoSelectItems
}) {
  const [params] = useSearchParams();
  const currentLocation = useLocation();
  const queuedSetups = currentLocation.state?.queuedSetups ?? [];
  const isHeldOrder = params.get("from") === "held";
  const isCurrent = params.get("current") === "true";
  const isSubmitted = params.get("from") === "submitted";
  const canHold =
    params.get("current")?.length || (residentId === 0 && !isSubmitted) || isHeldOrder; // any resident meal or guest (only current) or previously held
  const fromSP = isHeldOrder || isSubmitted;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedDiningAreaId, setSelectedDiningAreaId] = useState("");
  const [selectedTableName, setSelectedTableName] = useState("");
  const [outOfBuilding, setOutOfBuilding] = useState(false);
  const [declinedMeal, setDeclinedMeal] = useState(false);
  const [orderInfo, setOrderInfo] = useState({
    menuItems: [],
    alternateItems: [],
    order: {},
  });

  const [selectedSubRecipes, setSelectedSubRecipes] = useState([]);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");

  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalTitle, setReasonModalTitle] = useState(false);
  const [isSubmitAfterReason, setIsSubmitAfterReason] = useState(false);

  const [noteText, setNoteText] = useState("");
  const [addlCharges, setAddlCharges] = useState(0.0);
  const [selectedAddlCharges, setSelectedAddlCharges] = useState([]);
  const [customChargeText, setCustomChargeText] = useState("");

  const guestInfo = useSelector(selectGuestInfo);
  const orderWorkflow = useSelector(selectOrderWorkflow);
  const lastMealSelected = useSelector(selectLastMealSelected);
  const [isNoCharge, setIsNoCharge] = useState(false);
  const [hasShownPlanLimitModal, setHasShownPlanLimitModal] = useState(false);
  const [planLimitModalOpen, setPlanLimitModalOpen] = useState(false);

  useEffect(() => {
    //if refreshed during guest order, not an edit from hold (fromSP), send user back to guest info page to start over
    if (
      !guestInfo?.guestName &&
      currentLocation.pathname.includes("/takeorder/guest/order") &&
      !fromSP
    ) {
      navigate(`/takeorder/guest`);
    }
  }, [guestInfo]);

  const storedMenuInfo = useSelector(selectMenuInfo);

  // restoring a held/submitted order also restores the OOB/declined-meal flags — the rest of the
  // stored order's menu selections are restored inside OrderMenuDetails
  useEffect(() => {
    if (fromSP && storedMenuInfo) {
      setDeclinedMeal(!!storedMenuInfo?.order?.declinedMeal);
      setOutOfBuilding(!!storedMenuInfo?.order?.outOfBuilding);
    }
  }, [storedMenuInfo]);

  const { data: pendingMeals } = useGetPendingMealsQuery(false);

  const { data: diningAreas, isFetching: fetchingAreas } = useGetDiningAreasQuery();

  const { effectiveMenuId, effectiveSetupId, setupWarning, currentMealName, currentMealId } = useMemo(() => {
    if (!selectedDiningAreaId) return { effectiveMenuId: null, effectiveSetupId: null, setupWarning: "", currentMealName: null, currentMealId: null };

    const diningAreaName = diningAreas?.find(a => a.id === selectedDiningAreaId)?.name;

    // for guests, always use the active setup for the selected dining area — no warning
    if (isGuest) {
      const active = findActiveMealForDiningArea(pendingMeals, selectedDiningAreaId);
      return { effectiveMenuId: active?.setup?.menuId ?? null, effectiveSetupId: active?.setup?.id ?? null, setupWarning: "", currentMealName: active?.meal?.mealName ?? null, currentMealId: active?.meal?.mealId ?? null };
    }

    const selectedMeal = queuedSetups?.length
      ? findMealBySetupId(pendingMeals, queuedSetups[0]?.id)
      : null;

    // editing an existing held/submitted order — resolved by the order's own stored setup id, so it
    // doesn't need to still be the currently active meal for the dining area
    const heldOrderMeal = fromSP
      ? pendingMeals?.find(m => m.queuedSetups?.some(s => s.id === storedMenuInfo?.order?.tableSideSetupId))
      : null;

    const targetMeal = fromSP
      ? (heldOrderMeal ?? pendingMeals?.find(m => m.isActive))
      : (selectedMeal ?? pendingMeals?.find(m => m.isActive));

    if ((queuedSetups?.length && selectedMeal) || heldOrderMeal) {
      const { setup, isValid, message } = getMealSetupStatus(
        pendingMeals,
        targetMeal,
        selectedDiningAreaId,
        diningAreaName,
        queuedSetups?.length ? queuedSetups : undefined,
        true
      );
      if (setup?.menuId) {
        return { effectiveMenuId: setup.menuId, effectiveSetupId: setup.id, setupWarning: isValid ? "" : message, currentMealName: targetMeal?.mealName ?? null, currentMealId: targetMeal?.mealId ?? null };
      }
      if (!isValid) {
        return { effectiveMenuId: null, effectiveSetupId: null, setupWarning: message, currentMealName: targetMeal?.mealName ?? null, currentMealId: targetMeal?.mealId ?? null };
      }
    }

    if (!pendingMeals?.length) return { effectiveMenuId: null, effectiveSetupId: null, setupWarning: "", currentMealName: null, currentMealId: null };

    const { setup, isValid, message } = getMealSetupStatus(
      pendingMeals,
      targetMeal,
      selectedDiningAreaId,
      diningAreaName
    );

    return {
      effectiveMenuId: setup?.menuId ?? null,
      effectiveSetupId: setup?.id ?? null,
      setupWarning: isValid ? "" : message,
      currentMealName: targetMeal?.mealName ?? null,
      currentMealId: targetMeal?.mealId ?? null,
    };
  }, [selectedDiningAreaId, queuedSetups, pendingMeals, diningAreas, isGuest, fromSP, storedMenuInfo?.order?.tableSideSetupId]);

  // freeze setup/area for the query — only changes when effectiveMenuId changes, not on every area switch
  const stableQueryParams = useMemo(() => ({
    setupId: effectiveSetupId ?? tableSideSetupId,
    diningAreaId: selectedDiningAreaId,
  }), [effectiveMenuId]);

  // true while effectiveMenuId/pendingMeals are still resolving — treat as loading, not "no menu found"
  const isMenuQuerySkipped = !stableQueryParams.diningAreaId || (!isGuest && (!residentId || !stableQueryParams.setupId));

  const { data: menuInfo, isFetching: fetchingMenu } = useGetMenuQuery(
    {
      residentId: residentId,
      setupId: stableQueryParams.setupId,
      diningAreaId: stableQueryParams.diningAreaId,
      isGuest: !!isGuest,
      includeDiningAreas: true,
      guestResidentId:
        isGuest && guestInfo?.selectedResident
          ? guestInfo?.selectedResident
          : 0,
      guestName: isGuest ? guestInfo?.guestName?.trim() : "",
      guestBillingTypeId: isGuest ? guestInfo?.guestBillingTypeId : 0,
    },
    { skip: isMenuQuerySkipped }
  );

  const {
    data: chargeData,
    isFetching: chargeFetching,
    isSuccess: chargeSuccess
  } = useGetFacilityGuestChargeByIdQuery(guestInfo?.guestBillingTypeId, {
    refetchOnMountOrArgChange: true, skip: !guestInfo?.guestBillingTypeId
  });

  useEffect(() => {
    if (!chargeFetching && chargeSuccess && chargeData && isGuest) {
      const isNoCharge = chargeData?.chargeText?.trim()?.toUpperCase() == 'NO CHARGE';
      setIsNoCharge(isNoCharge);
    } else {
      setIsNoCharge(false);
    }
  }, [chargeFetching, chargeSuccess, isGuest])

  const { data: mealAddons, isFetching: fetchingAddons } =
    useGetMealAddOnsQuery(
      {
        residentId: residentId,
        setupId: stableQueryParams.setupId,
        diningAreaId: stableQueryParams.diningAreaId,
        isGuest: !!isGuest,
      },
      { skip: !residentId || !stableQueryParams.setupId || !stableQueryParams.diningAreaId || isGuest }
    );

  const {
    data: residents,
    isFetching: fetchingResidents,
    // refetch: refetchResidentList
  } = useGetResidentsAllQuery();

  const showPhoto = !!residents?.find(
    (resident) => resident.id == residentId
  )?.photoOnTrayCard;

  const { data: activeMeals } =
    useGetResidentActiveMealsByIdQuery(residentId, {
      refetchOnMountOrArgChange: true,
      skip: !residentId,
    });

  const { data: residentDetail, isFetching: fetchingResidentDetail } =
    useGetResidentInfoByIdQuery(
      { residentId, setupId: tableSideSetupId },
      { skip: !residentId || !tableSideSetupId || !!isGuest }
    );

  // fetched early for guest of residents
  // so it's ready by the time the confirm modal opens and kept out of residentInfo/header below
  const { data: guestResidentDetail } = useGetResidentInfoByIdQuery(
    { residentId: guestInfo?.selectedResident, setupId: effectiveSetupId },
    { skip: !isGuest || !chargeData?.residentMealPlanCharge || !guestInfo?.selectedResident || !effectiveSetupId }
  );

  const isResidentLoading = fetchingResidentDetail || fetchingAreas || fetchingResidents;
  const isMenuLoading = fetchingMenu || fetchingAddons || isMenuQuerySkipped;

  // normalizes residentDetail field names to match menuInfo shape for the info table
  const residentInfo = useMemo(() => {
    if (!fromSP && !isGuest && residentDetail) {
      return {
        dietName: residentDetail.diet,
        dietTextureName: residentDetail.dietTexture,
        dietOther: residentDetail.dietOther,
        liquidConsistency: residentDetail.liquidConsistency,
        fluidRestriction: residentDetail.fluidRestrictions,
        dislikes: residentDetail.dislikes,
        allergies: residentDetail.allergies,
        adaptiveEquipment: residentDetail.adaptiveEquipment,
        notes: residentDetail.notes ? [residentDetail.notes] : [],
        dietPrecautionsNote: residentDetail.dietPrecautionsNote,
        isClearLiquid: residentDetail.isClearLiquid,
        isFullLiquid: residentDetail.isFullLiquid,
        isLiquidized: residentDetail.isLiquidized,
        mealPlans: residentDetail.mealPlans,
        mealCostUsage: residentDetail.mealCostUsage,
        mealCountUsage: residentDetail.mealCountUsage
      };
    }
    if (fromSP) {
      // storedMenuInfo fields are available immediately; liquid flags come from menuInfo once loaded, merge with resident detail to fill gaps as needed
      return {
        dietName: storedMenuInfo?.dietName || residentDetail?.diet,
        dietTextureName: storedMenuInfo?.dietTextureName || residentDetail?.dietTexture,
        dietOther: storedMenuInfo?.dietOther || residentDetail?.dietOther,
        liquidConsistency: storedMenuInfo?.liquidConsistency || residentDetail?.liquidConsistency,
        fluidRestriction: storedMenuInfo?.fluidRestriction || residentDetail?.fluidRestrictions,
        dislikes: storedMenuInfo?.dislikes || residentDetail?.dislikes,
        allergies: storedMenuInfo?.allergies || residentDetail?.allergies,
        adaptiveEquipment: storedMenuInfo?.adaptiveEquipment || residentDetail?.adaptiveEquipment,
        notes: storedMenuInfo?.notes ? storedMenuInfo?.notes : residentDetail?.notes ? [residentDetail.notes] : [],
        dietPrecautionsNote: menuInfo?.dietPrecautionsNote,
        isClearLiquid: menuInfo?.isClearLiquid,
        isFullLiquid: menuInfo?.isFullLiquid,
        isLiquidized: menuInfo?.isLiquidized,
      };
    }
    return menuInfo;
  }, [fromSP, isGuest, residentDetail, storedMenuInfo, menuInfo]);

  // pre-populate header immediately from stored selection when possible
  useEffect(() => {
    if (!isGuest && !fromSP && lastMealSelected?.mealName) {
      dispatch(setSelectedMeal(lastMealSelected.mealName));
    }
  }, []);

  useEffect(() => {
    if (fromSP && !isGuest && storedMenuInfo?.order?.tableSideSetupId && pendingMeals?.length) {
      const match =
        pendingMeals.find(m => m.queuedSetups?.some(s => s.id === storedMenuInfo.order.tableSideSetupId))
        ?? pendingMeals.find(m => m.isActive);
      if (match) {
        dispatch(setSelectedMeal(`${match.mealName} (${dayjs(match.diningDate).format('ddd [-] MM/DD/YYYY')})`));
      }
    }
  }, [pendingMeals]); 

  useEffect(() => {
    // set dining area as soon as residentDetail is available, unblocking the menu query
    if (!fromSP && residentDetail && diningAreas) {
      const diningAreaId =
        residentDetail.diningAreaId > 0
          ? residentDetail.diningAreaId
          : diningAreas[0]?.id || "";
      selectDiningArea(diningAreaId);
      selectTable(residentDetail.diningAreaId > 0 ? residentDetail.diningTable ?? "" : "");
    } else if (fromSP && storedMenuInfo?.order?.diningAreaId) {
      selectDiningArea(storedMenuInfo.order.diningAreaId);
      selectTable(storedMenuInfo.order.tableName);
    }
  }, [residentDetail, diningAreas, storedMenuInfo]);

  useEffect(() => {
    if (isGuest && !fromSP && diningAreas?.length && !selectedDiningAreaId) {
      selectDiningArea(diningAreas[0].id);
    }
  }, [diningAreas]);

  useEffect(() => {
    if (isGuest && selectedDiningAreaId && pendingMeals?.length) {
      // prefer the meal whose setup for this specific area is active; fall back to any active meal
      const match =
        pendingMeals.find((m) =>
          m.queuedSetups?.some((s) => s.diningAreaId === selectedDiningAreaId && s.isActive)
        ) ?? pendingMeals.find((m) => m.isActive);
      if (match) {
        const matchingSetup =
          match.queuedSetups?.find(s => s.diningAreaId === selectedDiningAreaId && s.isActive) ??
          match.queuedSetups?.find(s => s.isActive) ??
          match.queuedSetups?.[0];
        const formattedName = `${match.mealName} (${dayjs(match.diningDate).format('ddd [-] MM/DD/YYYY')})`;
        dispatch(setSelectedMeal(formattedName));
        dispatch(setLastMealSelected({ setupId: matchingSetup?.id, mealName: formattedName }));
      }
    }
  }, [selectedDiningAreaId, pendingMeals]);

  useEffect(() => {
    if (menuInfo) {
      const relatedSetupIds = new Set(queuedSetups?.map(s => s.id) ?? [effectiveSetupId]);
      let activeMeal;
      activeMeals?.days?.forEach((day) => {
        day?.meals?.forEach((meal) => {
          if (meal?.setupId == effectiveSetupId) {
            activeMeal = meal;
          }
        });
      });

      const orderAlreadyTaken = activeMeal?.orderTaken ||
        activeMeals?.days?.some((day) =>
          day?.meals?.some((meal) => relatedSetupIds.has(meal?.setupId) && meal?.orderTaken)
        );

      const costPlan = residentDetail?.mealPlans?.find((plan) => plan.mealPlanId === 1);
      const countPlan = residentDetail?.mealPlans?.find((plan) => plan.mealPlanId === 2);

      const planCountLimitReached = !!countPlan && countPlan.mealCountLimit != null && countPlan.mealCountLimit <= residentDetail?.mealCountUsage;
      const planCostLimitReached = costPlan?.remainingCostBalance != null && costPlan?.remainingCostBalance < 0;

      if (orderAlreadyTaken && !fromSP) {
        setModalTitle("ALERT");
        setModalText(
          "You have already taken an order for this person.\nThis order will be sent in addition to the original order.\nIf you want to reprint the original order, go to the 'Submitted' orders tab."
        );
        setInfoModalOpen(true);
      } else if ((planCountLimitReached || planCostLimitReached) && !fromSP && !hasShownPlanLimitModal) {
        setPlanLimitModalOpen(true);
        setHasShownPlanLimitModal(true);
      }
    }
  }, [menuInfo, activeMeals]);

  const handleModalClose = () => {
    setInfoModalOpen(false);
  };

  // plan reset always lands on the 1st of the upcoming month, for now...
  const planLimitModalProps = useMemo(() => {
    const costPlan = residentDetail?.mealPlans?.find((plan) => plan.mealPlanId === 1);
    const countPlan = residentDetail?.mealPlans?.find((plan) => plan.mealPlanId === 2);
    const showCost = costPlan?.remainingCostBalance != null && costPlan.remainingCostBalance < 0;
    const showCount = !!countPlan && countPlan.mealCountLimit != null && countPlan.mealCountLimit <= residentDetail?.mealCountUsage;

    if (!showCost && !showCount) return null;

    const resetDate = dayjs().add(1, "month").startOf("month");
    const daysUntilReset = resetDate.diff(dayjs().startOf("day"), "day");

    return {
      resetDateLabel: resetDate.format("M/D/YY"),
      daysUntilReset,
      cost: showCost
        ? {
            usedValue: `$${(costPlan?.mealCostLimit ?? 0).toFixed(2)}`,
            remainingValue: `$${(costPlan?.remainingCostBalance ?? 0).toFixed(2)}`,
          }
        : null,
      count: showCount
        ? {
            usedValue: `${residentDetail?.mealCountUsage ?? 0} / ${countPlan?.mealCountLimit ?? 0}`,
            remainingValue: `${(countPlan?.mealCountLimit ?? 0) - (residentDetail?.mealCountUsage ?? 0)}`,
          }
        : null,
    };
  }, [residentDetail]);

  const handlePlanLimitModalClose = (canContinue) => {
    setPlanLimitModalOpen(false);
    if (!canContinue) {
      navigate(`/takeorder/residents`);
    }
  };

  const selectDiningArea = (diningAreaId) => {
    setSelectedDiningAreaId(() => diningAreaId);
    setSelectedTableName("");
  };

  const selectTable = (tableName) => {
    setSelectedTableName(tableName);
    setOrderInfo((prev) => ({
      ...prev,
      order: {
        ...prev.order,
        tableName: tableName,
      },
    }));
  };

  const handleOutOfBuildingChange = () => {
    setOutOfBuilding((prev) => !prev);
    setDeclinedMeal(false);
  };

  const handleDeclinedMealChange = () => {
    setDeclinedMeal((prev) => !prev);
    setOutOfBuilding(false);
  };

  const handleSubmit = (confirmType) => {
    // enforce dining area selection when not declining or out of building
    if (!selectedDiningAreaId && !outOfBuilding && !declinedMeal) {
      setModalTitle(""); // reset just in case other with title has been used
      setModalText("Dining Area cannot be blank.");
      setInfoModalOpen(true);
      return;
    }

    //guard clause for custom charge fields
    if (
      addlCharges > 100 ||
      (showAdditionalCharges &&
        (!customChargeText?.length || customChargeText?.length > 32))
    ) {
      return;
    }

    const tempMenuItems = [...orderInfo.menuItems];
    const tempAltItems = [...orderInfo.alternateItems];

    //force item selection when not declined/OOB
    if (
      !declinedMeal &&
      !outOfBuilding &&
      !tempMenuItems?.length &&
      !tempAltItems?.length
    ) {
      setModalTitle("");
      setModalText("Order must contain at least 1 item.");
      setInfoModalOpen(true);
      return;
    }

    tempMenuItems.forEach((item) => {
      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId === item.itemId && !sub.isAlternateItem
      );
      item["subItemList"] = [...subRecipes];
      item["guestPrice"] = isNoCharge ? 0 : item.guestPrice;
    });

    tempAltItems.forEach((item) => {
      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId === item.itemId && sub.isAlternateItem
      );
      item["subItemList"] = [...subRecipes];
      item["guestPrice"] = isNoCharge ? 0 : item.guestPrice;
    });

    //update fields we removed from onChange for performance reasons (large object re-writes)
    //and put menu/alt items back with selected subrecipes bound to parent
    setOrderInfo((prev) => ({
      ...prev,
      guestPrice: isNoCharge ? 0 : prev.guestPrice,
      menuItems: [...tempMenuItems],
      alternateItems: [...tempAltItems],
      order: {
        ...prev.order,
        additionalCharges: isNoCharge ? 0 : addlCharges,
        additionalChargeNote: customChargeText,
        declinedMeal: declinedMeal,
        diningAreaId: selectedDiningAreaId || 0, //fallback for declined/OOB (call fails as null), otherwise selection is enforced anyway
        // stamped from effectiveSetupId (not the frozen menu-query setupId) so switching dining areas under the same menu still submits the matching setup
        tableSideSetupId: effectiveSetupId ?? storedMenuInfo?.order?.tableSideSetupId ?? tableSideSetupId,
        guest: isGuest ? guestInfo?.guestName?.trim() : null,
        guestBillingTypeId: isGuest ? guestInfo?.guestBillingTypeId : 0,
        guestTypeId: isGuest ? guestInfo?.selectedGuestType : 0,
        employeeId: isGuest ? guestInfo?.employeeId : null,
        note: noteText,
        outOfBuilding: outOfBuilding,
        tableName: selectedTableName || "",
        orderCharges: [
          ...selectedAddlCharges.map((charge) => {
            return {
              ...charge,
              //needs to match held/edited order submission schema
              facilityChargeTypeId: charge.id,
              chargeName:
                charge.chargeTypeid === 6
                  ? charge.chargeTypeText
                  : charge.chargeTypeName,
              price: isNoCharge ? 0 : charge.price
            };
          }),
        ],
        residentId: isGuest ? null : +residentId,
        adaptiveEquipment: prev.adaptiveEquipment ?? "",
        allergies: prev.allergies ?? "",
        fluidRestriction: prev.fluidRestriction ?? "",
        mealAddons: mealAddons?.join("; ") ?? "",
      },
    }));

    // run this after order has been updated to short circuit confirm modal
    if (outOfBuilding || declinedMeal) {
      setReasonModalTitle(outOfBuilding ? "Out of Building" : "Declined Meal");
      setReasonModalOpen(true);
      return;
    }

    setConfirmType(confirmType);
    setConfirmModalOpen(true);
  };

  const [addHoldOrder, { isSuccess: holdSuccess, isError: holdError }] =
    useAddHeldOrderMutation();

  useEffect(() => {
    if (holdSuccess && !holdError) {
      dispatch(setSelectedMeal("")); //reset title bar
      //if reholding a previously held order, clean up old order
      if (isHeldOrder) {
        deleteHeldOrder(storedMenuInfo?.order?.id);
      }
      if (isGuest) {
        navigate(`/takeorder/guest`);
      } else {
        if (!orderWorkflow || orderWorkflow === "Meal") {
          navigate(`/takeorder/residents`);
        } else if (orderWorkflow === "Resident") {
          navigate(`/takeorder/residents/meals/${residentId}`);
        }
      }
    }
  }, [holdSuccess, holdError]);

  const [addNewOrder, { isSuccess: orderSuccess, isError: orderError }] =
    useAddResidentOrderMutation();

  useEffect(() => {
    if (orderSuccess && !orderError) {
      dispatch(setSelectedMeal("")); //reset title bar
      if (fromSP) {
        // attempt to clean up on submit when stored data present
        if (isHeldOrder) {
          deleteHeldOrder(storedMenuInfo?.order?.id);
        } else if (isSubmitted) {
          deleteOrder({ orderId: storedMenuInfo?.order?.id, isAdmin: false });
        }
      } else {
        if (isGuest) {
          navigate(`/takeorder/guest`);
        } else {
          if (!orderWorkflow || orderWorkflow === "Meal") {
            navigate(`/takeorder/residents`);
          } else if (orderWorkflow === "Resident") {
            navigate(`/takeorder/residents/meals/${residentId}`);
          }
        }
      }
    }
  }, [orderSuccess, orderError]);

  const [deleteHeldOrder, { isSuccess: deleteHeldSuccess }] =
    useDeleteHeldOrderMutation();
  const [deleteOrder, { isSuccess: deleteOrderSuccess }] =
    useDeleteOrderMutation();

  useEffect(() => {
    if (deleteHeldSuccess || deleteOrderSuccess) {
      navigate(`/takeorder/residents`);
    }
  }, [deleteHeldSuccess, deleteOrderSuccess]);

  const handleConfirmClose = (canContinue) => {
    setConfirmModalOpen(false);

    if (canContinue) {
      if (confirmType === "HOLD") {
        addHoldOrder(orderInfo);
      } else if (confirmType === "SUBMIT") {
        if (isDirectPrint) {
          const html = document.getElementById("printSection").cloneNode(true);
          handlePrint(html);
        }

        addNewOrder(orderInfo);
      }
    }
  };

  const handleReasonClose = (canContinue, reasonText = "") => {
    setReasonModalOpen(false);
    setReasonModalTitle("");

    if (canContinue) {
      setOrderInfo((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          note: reasonText,
        },
      }));
      setIsSubmitAfterReason(true);
    }
  };

  useEffect(() => {
    if (isSubmitAfterReason) {
      addNewOrder(orderInfo);
      setIsSubmitAfterReason(false);
    }
  }, [isSubmitAfterReason]); //due to async order update, have to use a hook

  const getResidentNameById = (residentId) => {
    return (
      residents
        ?.find((resident) => resident.id === residentId)
        ?.name?.split("(")?.[0]
        ?.trim() || ""
    );
  };

  const getResidentWingById = (residentId) => {
    return residents?.find((resident) => resident.id == residentId)?.wing || "";
  };

  const getDiningAreaNameById = (diningAreaId) => {
    return (
      diningAreas?.find((area) => area.id === diningAreaId)?.name?.trim() || ""
    );
  };

  const renderPrintItems = () => {
    const menuList = [];

    orderInfo?.menuItems?.forEach((item, idx) => {
      const quantity =
        item.quantity >= 1
          ? item.quantity.toFixed(0)
          : item.quantity.toFixed(1);
      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId == item.itemId
      );

      menuList.push(
        <div key={`${item.itemId}-${idx}`} style={printStyles.printDiv}>
          <span>
            {quantity} - <b>{item.name}</b>{" "}
            {`${item.portionSize ? `(${item.portionSize})` : ""}`}
          </span>
        </div>
      );

      if (item?.removedItems?.length) {
        item.removedItems.forEach((removed) => {
          menuList.push(
            <div
              key={`${removed.id}-${Math.floor(Math.random(0, 100) * 100)}`}
              style={{ paddingY: "0px", paddingLeft: "24px" }}>
              <span>
                <i>{`Remove - ${removed.text} (ALLERGY)`}</i>
              </span>
            </div>
          );
        });
      }

      if (item?.allergyNote) {
        menuList.push(
          <div
            key={`${Math.floor(Math.random(0, 100) * 100)}-allergyNote`}
            style={{ paddingY: "0px", paddingLeft: "24px" }}>
            <span>
              <i>{`${item.allergyNote}`}</i>
            </span>
          </div>
        );
      }

      if (item?.specialNote) {
        menuList.push(
          <div
            key={`${Math.floor(Math.random(0, 100) * 100)}-specialNote`}
            style={{ paddingY: "0px", paddingLeft: "24px" }}>
            <span>
              <b>{`Special Note: ${item.specialNote}`}</b>
            </span>
          </div>
        );
      }

      subRecipes.forEach((sub, idx) => {
        menuList.push(
          <div
            key={`${sub.recipeId}-${idx}`}
            style={{ paddingY: "0px", paddingLeft: "24px" }}>
            <span>{`Add ${sub.quantity} - ${sub.recipeName}`}</span>
          </div>
        );
      });
    });

    orderInfo?.alternateItems?.forEach((alt) => {
      const quantity =
        alt.quantity >= 1 ? alt.quantity.toFixed(0) : alt.quantity.toFixed(1);
      const subRecipes = selectedSubRecipes.filter(
        (sub) => sub.parentRecipeId == alt.itemId
      );

      menuList.push(
        <div key={alt.itemId} style={printStyles.printDiv}>
          <span>
            {quantity} - <b>{alt.name}</b>{" "}
            {`${alt.portionSize ? `(${alt.portionSize})` : ""}`}
          </span>
        </div>
      );

      if (alt?.removedItems?.length) {
        alt.removedItems.forEach((removed, idx) => {
          menuList.push(
            <div
              key={`${removed.id}-${idx}`}
              style={{ paddingY: "0px", paddingLeft: "24px" }}>
              <span>
                <i>{`Remove - ${removed.text} (ALLERGY)`}</i>
              </span>
            </div>
          );
        });
      }

      if (alt?.allergyNote) {
        menuList.push(
          <div
            key={`${alt.itemId}-allergyNote`}
            style={{ paddingY: "0px", paddingLeft: "24px" }}>
            <span>
              <i>{`${alt.allergyNote}`}</i>
            </span>
          </div>
        );
      }

      if (alt?.specialNote) {
        menuList.push(
          <div
            key={`${alt.itemId}-specialNote`}
            style={{ paddingY: "0px", paddingLeft: "24px" }}>
            <span>
              <b>{`Special Note: ${alt.specialNote}`}</b>
            </span>
          </div>
        );
      }

      subRecipes.forEach((sub) => {
        menuList.push(
          <div
            key={sub.recipeId}
            style={{ paddingY: "0px", paddingLeft: "24px" }}>
            <span>{`Add ${sub.quantity} - ${sub.recipeName}`}</span>
          </div>
        );
      });
    });

    return menuList;
  };

  const renderPrintContent = () => {
    const diningAreaName = getDiningAreaNameById(
      orderInfo?.order?.diningAreaId
    );
    const residentWing = getResidentWingById(residentId);
    const isIsolation = residents?.find(
      (res) => res.id == residentId
    )?.isIsolation;

    return (
      <div
        style={{
          padding: "10px 5px 5px 5px",
          fontSize: "15px",
          maxWidth: "300px",
        }}>
        <div
          style={{ fontSize: "1.25rem", fontWeight: "bold", paddingY: "5px" }}>
          {isGuest ? orderInfo?.order?.guest : orderInfo?.residentName}
        </div>
        {isIsolation && (
          <div style={{ fontWeight: "bold", paddingY: "5px" }}>
            * ISOLATION *
          </div>
        )}
        {(!!orderInfo?.order?.guestOfResidentId ||
          !!orderInfo?.order?.guest?.length) && (
          <div style={printStyles.printDiv}>
            <p>Guest/Employee: {orderInfo.order.guest}</p>
            {!!orderInfo.order.guestOfResidentId && (
              <p>
                Guest Of:
                {getResidentNameById(orderInfo.order.guestOfResidentId)}
              </p>
            )}
          </div>
        )}
        {isGuest && (
          <div style={printStyles.printDiv}>
            Payment Status: {chargeData?.chargeText ?? ""}
          </div>
        )}
        {diningAreaName !== "" && (
          <div style={printStyles.printGeneral}>
            <span
              style={{
                marginRight: ".75rem",
                fontWeight: "bold",
                fontSize: ".875rem",
              }}>
              {diningAreaName.replace(/Dining Room/gi, "DR")}
            </span>
            <span style={{ fontWeight: "bold", fontSize: ".875rem" }}>
              {!DEFAULT_EMPTY.includes(orderInfo?.order?.tableName)
                ? `Table: ${orderInfo.order.tableName}`
                : null}
            </span>
          </div>
        )}
        {!DEFAULT_EMPTY.includes(orderInfo?.roomBed) && (
          <div style={printStyles.printGeneral}>
            <span style={{ fontSize: ".75rem", marginBottom: ".5rem" }}>
              {`Wing: ${residentWing || "N/A"}`}
            </span>
            <span style={{ margin: "0 .5rem", fontSize: ".75rem" }}>|</span>
            <span style={{ fontSize: ".75rem" }}>
              {`Room: ${orderInfo.roomBed}`}
            </span>
          </div>
        )}

        <hr style={{ marginBottom: "6px", border: "1px solid black" }} />

        <div style={{ display: "flex", fontSize: ".675rem" }}>
          <div id="label-col" style={{ minWidth: "75px" }}>
            {!DEFAULT_EMPTY.includes(orderInfo.dietName) && <div>Diet:</div>}
            {!DEFAULT_EMPTY.includes(orderInfo.dietTextureName) && (
              <div>Texture:</div>
            )}
            {!DEFAULT_EMPTY.includes(orderInfo.dietOther) && <div>Other:</div>}
            {!DEFAULT_EMPTY.includes(orderInfo.liquidConsistency) && (
              <div>Liquid:</div>
            )}
            {!DEFAULT_EMPTY.includes(orderInfo.allergies) && (
              <div>
                <i>Allergies:</i>
              </div>
            )}
          </div>
          <div id="value-col" style={{ whiteSpace: "pre" }}>
            {!DEFAULT_EMPTY.includes(orderInfo.dietName) && (
              <div>
                <b>{orderInfo.dietName}</b>
              </div>
            )}
            {!DEFAULT_EMPTY.includes(orderInfo.dietTextureName) && (
              <div>
                <b>{orderInfo.dietTextureName}</b>
              </div>
            )}
            {!DEFAULT_EMPTY.includes(orderInfo.dietOther) && (
              <div>
                <b>{orderInfo.dietOther}</b>
              </div>
            )}
            {!DEFAULT_EMPTY.includes(orderInfo.liquidConsistency) && (
              <div>
                <b>{orderInfo.liquidConsistency}</b>
              </div>
            )}
            {!DEFAULT_EMPTY.includes(orderInfo.allergies) && (
              <div>
                <i>{orderInfo.allergies}</i>
              </div>
            )}
          </div>
        </div>

        <hr style={{ marginTop: "6px", border: "1px solid black" }} />

        <div
          style={{
            marginTop: "1rem",
            fontSize: ".75rem",
            marginBottom: "1rem",
          }}>
          {renderPrintItems()}
          <div style={printStyles.printGeneral}>
            <i>Added Notes: {noteText || ""}</i>
          </div>
        </div>

        <hr style={{ marginBottom: "6px", border: "1px solid black" }} />

        <div style={{ marginBottom: "1.5rem", fontSize: ".75rem"}}>
          {allowMealAddons && !!mealAddons?.length && (
            <div style={printStyles.printGeneral}>
              Meal Addons: {mealAddons?.join("; ")}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "1.5rem", fontSize: ".75rem" }}>
          {allowMealCardNotes && !!orderInfo.notes?.length && (
            <div style={printStyles.printGeneral}>
              MealCard Notes: {orderInfo?.notes.join(", ")}
            </div>
          )}
          {!DEFAULT_EMPTY.includes(orderInfo?.adaptiveEquipment) && (
            <div style={printStyles.printGeneral}>
              Adaptive Equip: {orderInfo?.adaptiveEquipment}
            </div>
          )}
          {!DEFAULT_EMPTY.includes(orderInfo?.fluidRestriction) && (
            <div style={printStyles.printGeneral}>
              Fluid Restrict: {orderInfo?.fluidRestriction}
            </div>
          )}
        </div>

        <div style={{ fontSize: ".75rem" }}>
          <div>
            <span>
              <b>Total Price:</b> ${(
                (isNoCharge ? 0 : (orderInfo?.guestPrice ?? 0)) + 
                (isNoCharge ? 0 : (orderInfo?.addlCharges ?? 0)) + 
                (selectedAddlCharges?.length  && !isNoCharge ? selectedAddlCharges.reduce((acc, charge) => acc + charge.price, 0) : 0))
                ?.toFixed(2)}
            </span>
          </div>
          <div>
            <span>
              <b>Signature: __________________________________</b>
            </span>
          </div>
        </div>
        <div
          style={{
            fontSize: "1.25rem",
            fontWeight: "bold",
            textAlign: "center",
          }}>
          {orderInfo.order.residentId === 0
            ? orderInfo.order.guest
            : orderInfo.residentName}
        </div>
        <div style={{ marginTop: "5px", paddingY: "5px", fontSize: "10px" }}>
          {`Order Taken: ${new Date().toLocaleString(
            "en-US",
            DIRECT_PRINT_DATE_OPTIONS
          )}, by ${currentUserData?.firstName || ""} ${
            currentUserData?.lastName || ""
          }`}
        </div>
      </div>
    );
  };

  const getBannerName = () => {
    if (isGuest) return guestInfo?.guestName;
    if (!fromSP) return residentDetail?.name;
    return storedMenuInfo?.residentName || menuInfo?.residentName;
  };

  const getBannerRoomBed = () => {
    if (isGuest) return {};
    if (!fromSP && residentDetail) return { roomNumber: residentDetail.roomNumber, bed: residentDetail.bed };
    const roomBed = (storedMenuInfo?.roomBed || menuInfo?.roomBed)?.split("/");
    return { roomNumber: roomBed?.[0], bed: roomBed?.[1] };
  };

  return (
    <>
      {" "}
      {isResidentLoading ? (
        <EditSkeleton />
      ) : (
        <div className="paperContent customScrollbar">
          <ResidentDetailSection
            residentId={residentId}
            residentInfo={residentInfo}
            diningAreas={diningAreas}
            selectedDiningAreaId={selectedDiningAreaId}
            onDiningAreaChange={selectDiningArea}
            selectedTableName={selectedTableName}
            onTableChange={selectTable}
            outOfBuilding={outOfBuilding}
            onOutOfBuildingChange={handleOutOfBuildingChange}
            declinedMeal={declinedMeal}
            onDeclinedMealChange={handleDeclinedMealChange}
            mealAddons={mealAddons}
            menuInfo={menuInfo}
            showPhoto={showPhoto}
            residentName={getBannerName()}
            roomBed={getBannerRoomBed()}
            guestInfo={guestInfo}
          />

          {setupWarning && (
            <WarningText text={setupWarning} color="red" />
          )}

          {!setupWarning && !menuInfo && !isMenuQuerySkipped && !isMenuLoading && !isGuest && (
            <>
              <WarningText
                text={"Unable to load order. Please ensure the resident is assigned to a dining area before proceeding."}
              />
              <Box display="flex" justifyContent="center" alignItems="center" marginBlock="10px">
                <BaseButton text={"Go Back"} onClick={() => navigate(-1)} />
              </Box>
            </>
          )}

          <OrderMenuDetails
            residentId={residentId}
            menuInfo={menuInfo}
            isMenuLoading={isMenuLoading}
            isHidden={!!setupWarning || (!menuInfo && !isMenuQuerySkipped && !isMenuLoading && !isGuest)}
            existingOrder={storedMenuInfo}
            hasExistingOrder={fromSP}
            orderInfo={orderInfo}
            setOrderInfo={setOrderInfo}
            selectedSubRecipes={selectedSubRecipes}
            setSelectedSubRecipes={setSelectedSubRecipes}
            noteText={noteText}
            setNoteText={setNoteText}
            addlCharges={addlCharges}
            setAddlCharges={setAddlCharges}
            customChargeText={customChargeText}
            setCustomChargeText={setCustomChargeText}
            showAdditionalCharges={showAdditionalCharges}
            setShowAdditionalCharges={setShowAdditionalCharges}
            selectedAddlCharges={selectedAddlCharges}
            setSelectedAddlCharges={setSelectedAddlCharges}
            outOfBuilding={outOfBuilding}
            declinedMeal={declinedMeal}
            isAutoSelectItems={isAutoSelectItems}
            onFluidRestrictionWarning={() => {
              setModalTitle("Fluid Restriction");
              setModalText(
                "This resident has a fluid restriction.\nMake sure that this selection doesn't exceed the resident's restriction."
              );
              setInfoModalOpen(true);
            }}
          />
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100% !important", flex: 1 }}>
            {canHold && !outOfBuilding && !declinedMeal && (
              <Button
                variant="contained"
                sx={{
                  fontWeight: "bold",
                  marginRight: ".5rem",
                  width: "calc(50% - 1rem)",
                  backgroundColor: "var(--tealColor",
                }}
                onClick={() => handleSubmit("HOLD")}>
                Hold Ticket
              </Button>
            )}
            <Button
              variant="contained"
              color="#000"
              sx={{
                fontWeight: "bold",
                width:
                  !canHold || outOfBuilding || declinedMeal
                    ? "calc(100% - 2.5rem)"
                    : "calc(50% - 1rem)",
                backgroundColor: "#fdb92e",
              }}
              onClick={() => handleSubmit("SUBMIT")}>
              {fromSP
                ? "Submit / Reprint"
                : isGuest || (isCurrent && !outOfBuilding && !declinedMeal)
                ? "Print Now"
                : "Submit"}
            </Button>
          </Grid>
          {infoModalOpen && (
            <InfoModal
              title={modalTitle}
              open={infoModalOpen}
              modalText={modalText}
              handleClose={handleModalClose}
            />
          )}
          {planLimitModalOpen && planLimitModalProps && (
            <PlanLimitModal
              open={planLimitModalOpen}
              residentName={residentDetail?.name}
              {...planLimitModalProps}
              handleClose={handlePlanLimitModalClose}
            />
          )}
          {confirmModalOpen && (
            <OrderConfirmModal
              open={confirmModalOpen}
              orderData={orderInfo}
              confirmType={confirmType}
              selectedSubRecipes={selectedSubRecipes}
              handleClose={handleConfirmClose}
              isNoCharge={isNoCharge}
              isGuest={isGuest}
              residentMealPlanCharge={chargeData?.residentMealPlanCharge}
              mealPlans={isGuest ? guestResidentDetail?.mealPlans : residentDetail?.mealPlans}
              mealCountUsage={isGuest ? guestResidentDetail?.mealCountUsage : residentDetail?.mealCountUsage}
              mealCostUsage={isGuest ? guestResidentDetail?.mealCostUsage : residentDetail?.mealCostUsage}
              currentMealName={currentMealName}
              currentMealId={currentMealId}
            />
          )}
          {reasonModalOpen && (
            <ReasonModal
              open={reasonModalOpen}
              title={reasonModalTitle}
              handleClose={handleReasonClose}
            />
          )}
          <div
            id="printSection"
            style={{ position: "absolute", top: "-9999px" }}>
            {renderPrintContent()}
          </div>
        </div>
      )}
    </>
  );
}
